// skipcq: JS-W1028, JS-0128
import React, { useCallback, useMemo, useState } from 'react';
import { Box, Container, Tabs, Tab } from '@mui/material';
import DashboardLayout from '../../components/layout/DashboardLayout';
import TabPanel from '../../components/tabs/TabPanel';
import EventDefinitionsTab from './EventDefinitionsTab';
import EventImagesTab from './EventImagesTab';
import useEventDefinitionsList from '../../hooks/useEventDefinitionsList';
import useEventStorageImages from '../../hooks/useEventStorageImages';
import EditEventDialog from './EditEventDialog';
import useEventFormDialog from './useEventFormDialog';

/**
 * Events management: definitions in Firestore `eventDefinitions`, images in Storage `events/`.
 */
function Events() {
  const { events, loading, error, createEvent, updateEvent } =
    useEventDefinitionsList();
  const {
    files: storageFiles,
    fileNames,
    imageUrlByName,
    loading: storageLoading,
    error: storageError,
    refresh: refreshStorage,
    uploadFile,
    deleteImageIfUnused,
  } = useEventStorageImages();

  const allowedImageChoices = useMemo(
    () =>
      [...fileNames].sort((left, right) => left.localeCompare(right)),
    [fileNames],
  );

  const { editDialogProps, openAddDialog } = useEventFormDialog({
    allowedImageChoices,
    imagesLoading: storageLoading,
    createEvent,
    updateEvent,
  });

  const [activeTab, setActiveTab] = useState(0);

  const handleTabChange = useCallback((event, newValue) => {
    setActiveTab(newValue);
  }, []);

  return (
    <DashboardLayout title="Events">
      <Container fixed>
        <Box
          sx={{
            borderBottom: 1,
            borderColor: 'divider',
            mb: 2,
          }}
        >
          <Tabs
            value={activeTab}
            onChange={handleTabChange}
            aria-label="Events section tabs"
          >
            <Tab
              id="events-tab-0"
              aria-controls="events-tabpanel-0"
              label="Event definitions"
            />
            <Tab
              id="events-tab-1"
              aria-controls="events-tabpanel-1"
              label="Event images"
            />
          </Tabs>
        </Box>

        <TabPanel value={activeTab} index={0}>
          <EventDefinitionsTab
            loading={loading}
            error={error}
            onOpenAdd={openAddDialog}
            events={events}
            imageUrlByName={imageUrlByName}
          />
        </TabPanel>

        <TabPanel value={activeTab} index={1}>
          <EventImagesTab
            files={storageFiles}
            loading={storageLoading}
            error={storageError}
            refresh={refreshStorage}
            uploadFile={uploadFile}
            deleteImageIfUnused={deleteImageIfUnused}
          />
        </TabPanel>
      </Container>
      <EditEventDialog {...editDialogProps} />
    </DashboardLayout>
  );
}

export default Events;
